import { cn } from '@/lib/utils'
import { format as formatLocalized } from '@/utils/date-fns/format'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  eachDayOfInterval,
  endOfMonth,
  format,
  getISOWeek,
  startOfMonth,
} from 'date-fns'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { TONE_COLORS, formatNumber, gradeTone } from './metrics'
import { initialWeekIndex, monthWeeks, weekDays } from './weeks'

interface ChartDay {
  date: string
  grade: number
  distanceKm: number
}

interface ChartProps {
  month: Date
  days: ChartDay[]
  maximumGrade: number
  selected: string | null
  onSelect(date: string): void
}

// The grade scale starts near 50, so the bars start there too and the
// difference between drivers is not flattened into the top of the chart.
const AXIS_FLOOR = 40
const HEIGHT = 160
const SWIPE_PX = 40

const Axis: React.FC<{ top: number; labelRows: number }> = ({
  top,
  labelRows,
}) => (
  <div
    className="flex flex-col justify-between text-right text-[10px] tabular-nums text-stone-400"
    style={{ height: HEIGHT, marginBottom: labelRows * 16 + 4 }}
  >
    <span>{top}</span>
    <span>{(top + AXIS_FLOOR) / 2}</span>
    <span>{AXIS_FLOOR}</span>
  </div>
)

const Bars: React.FC<{
  dates: (Date | null)[]
  byDate: Map<string, ChartDay>
  top: number
  selected: string | null
  onSelect(date: string): void
  weekdays?: boolean
  gap: number
  /** Whether a swipe just ended, so the bar under it is not also tapped. */
  swiped?: React.RefObject<boolean>
}> = ({ dates, byDate, top, selected, onSelect, weekdays, gap, swiped }) => {
  const scale = (grade: number) =>
    ((Math.max(grade, AXIS_FLOOR) - AXIS_FLOOR) / (top - AXIS_FLOOR)) * HEIGHT

  return (
    <div
      className="grid flex-1"
      style={{
        gridTemplateColumns: `repeat(${dates.length}, minmax(0, 1fr))`,
        columnGap: gap,
      }}
    >
      {dates.map((date, index) => {
        if (!date) return <div key={index} />
        const key = format(date, 'yyyy-MM-dd')
        const day = byDate.get(key)
        const isSelected = key === selected

        return (
          <button
            key={key}
            type="button"
            disabled={!day}
            title={
              day
                ? `${format(date, 'dd.MM')}: ${formatNumber(
                    day.grade
                  )} · ${formatNumber(day.distanceKm, 0)} km`
                : undefined
            }
            onClick={() => {
              if (swiped?.current) return
              if (day) onSelect(key)
            }}
            className={cn(
              'group flex flex-col items-center gap-1',
              day ? 'cursor-pointer' : 'cursor-default'
            )}
          >
            <div
              className={cn(
                'relative flex w-full items-end justify-center rounded-md',
                isSelected ? 'bg-stone-200' : 'group-hover:bg-stone-100'
              )}
              style={{ height: HEIGHT }}
            >
              {day && (
                <div
                  className={cn(
                    'rounded-t',
                    weekdays ? 'w-2/3 max-w-10' : 'w-3/4 max-w-6'
                  )}
                  style={{
                    height: scale(day.grade),
                    backgroundColor: TONE_COLORS[gradeTone(day.grade)],
                    opacity: selected && !isSelected ? 0.45 : 1,
                  }}
                />
              )}
            </div>
            {weekdays && (
              <span className="text-[11px] capitalize text-stone-500">
                {formatLocalized(date, 'EEEEEE')}
              </span>
            )}
            <span
              className={cn(
                'tabular-nums',
                weekdays ? 'text-xs' : 'text-[10px]',
                isSelected
                  ? 'font-semibold text-stone-900'
                  : day
                  ? 'text-stone-600'
                  : 'text-stone-300'
              )}
            >
              {format(date, 'd')}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/** One week at a time, for screens too narrow to tell a month's days apart. */
const WeekPager: React.FC<
  ChartProps & { byDate: Map<string, ChartDay>; top: number }
> = ({ month, days, selected, onSelect, byDate, top }) => {
  const translations = useTranslate({
    week: 'common.week',
    previous: 'driver-grade.previous-week',
    next: 'driver-grade.next-week',
  })
  const weeks = useMemo(() => monthWeeks(month), [month])
  const [chosen, setChosen] = useState<number | null>(null)
  const index =
    chosen ??
    initialWeekIndex(
      month,
      selected,
      days.map(day => day.date)
    )
  const monday = weeks[index] ?? startOfMonth(month)
  const dates = weekDays(monday, month)
  const inMonth = dates.filter((date): date is Date => date !== null)
  const first = inMonth[0]
  const last = inMonth[inMonth.length - 1]

  const swipeStart = useRef<number | null>(null)
  const swiped = useRef(false)
  const go = (step: number) =>
    setChosen(Math.min(weeks.length - 1, Math.max(0, index + step)))

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label={translations.previous}
          disabled={index === 0}
          onClick={() => go(-1)}
          className="rounded-md p-1.5 text-stone-500 hover:bg-stone-100 disabled:opacity-30"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
        <div className="text-center text-sm">
          <span className="font-semibold text-stone-900">
            {translations.week} {getISOWeek(monday)}
          </span>
          {first && last && (
            <span className="ml-2 tabular-nums text-stone-500">
              {formatLocalized(first, 'd.')}–{formatLocalized(last, 'd. MMM')}
            </span>
          )}
        </div>
        <button
          type="button"
          aria-label={translations.next}
          disabled={index === weeks.length - 1}
          onClick={() => go(1)}
          className="rounded-md p-1.5 text-stone-500 hover:bg-stone-100 disabled:opacity-30"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </div>
      <div
        className="flex touch-pan-y gap-2 select-none"
        onPointerDown={event => {
          swipeStart.current = event.clientX
          swiped.current = false
        }}
        onPointerUp={event => {
          if (swipeStart.current === null) return
          const distance = event.clientX - swipeStart.current
          swipeStart.current = null
          if (Math.abs(distance) < SWIPE_PX) return
          swiped.current = true
          go(distance < 0 ? 1 : -1)
        }}
        onPointerCancel={() => {
          swipeStart.current = null
        }}
      >
        <Axis top={top} labelRows={2} />
        <Bars
          dates={dates}
          byDate={byDate}
          top={top}
          selected={selected}
          onSelect={onSelect}
          weekdays
          gap={8}
          swiped={swiped}
        />
      </div>
    </div>
  )
}

export const DriverGradeDaysChart: React.FC<ChartProps> = props => {
  const { month, days, maximumGrade, selected, onSelect } = props
  const byDate = useMemo(
    () => new Map(days.map(day => [day.date, day])),
    [days]
  )
  const dates = useMemo(
    () =>
      eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) }),
    [month]
  )
  const top = Math.ceil(maximumGrade / 10) * 10

  return (
    <>
      <div className="hidden gap-2 md:flex">
        <Axis top={top} labelRows={1} />
        <Bars
          dates={dates}
          byDate={byDate}
          top={top}
          selected={selected}
          onSelect={onSelect}
          gap={3}
        />
      </div>
      <div className="md:hidden">
        <WeekPager {...props} byDate={byDate} top={top} />
      </div>
    </>
  )
}
